import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export const usePurgeUnminted = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const changeOpen = (next) => {
    if (inFlight.current) return;
    if (!next && preview?.confirmation_token) {
      api.delete("/admin/templates/purge-unminted/confirmation", {
        data: { confirmation_token: preview.confirmation_token },
      }).catch(() => { /* The unused approval also expires server-side. */ });
    }
    setPin("");
    setPreview(null);
    setError("");
    setOpen(next);
  };

  const verify = async (event) => {
    event.preventDefault();
    if (inFlight.current || !/^[0-9]{4,10}$/.test(pin)) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/templates/purge-unminted/prepare", { pin });
      setPreview(data);
    } catch (e) {
      setError(apiError(e, "Passcode verification failed. Nothing was removed."));
    } finally {
      setPin("");
      inFlight.current = false;
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (inFlight.current || !preview?.confirmation_token || !preview.count) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/templates/purge-unminted", {
        confirmation_token: preview.confirmation_token, confirmed: true,
      });
      ["admin-templates", "admin-listings", "listings", "home-featured"].forEach((key) => {
        qc.invalidateQueries({ queryKey: [key] });
      });
      toast.success(data.deleted ? `Removed ${data.deleted} unminted template${data.deleted === 1 ? "" : "s"}` : "No unminted templates removed", {
        id: "purge-unminted-result",
        description: data.skipped ? `${data.skipped} reviewed templates were no longer eligible or had already been removed.` : undefined,
      });
      setOpen(false);
      setPreview(null);
    } catch (e) {
      // A submitted approval may have been consumed even if the response was
      // lost. Require fresh verification instead of replaying a deletion.
      setPreview(null);
      setError(`${apiError(e, "Removal could not be confirmed.")} Review the templates before trying again.`);
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return { open, changeOpen, pin, setPin, preview, error, busy, verify, confirm };
};